/**
 * Gigs tab content: loading, job cards list, gigs pagination
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Loader } from '@/components/ui';
import { cn } from '../../../utils/cn';
import type { JobCardData } from '../../../types/jobs';
import type { EventPricingApiItem } from '../../../services/artistService';
import type { JobsTypeFilter } from '../../../stores/jobsGigsStore';
import type { GigsTypeTab } from './gigsTypeConfig';
import { JobCardsList } from './JobCardsList';

const GIGS_PAGE_SIZE = 10;

export interface GigsTabContentProps {
  jobs: JobCardData[];
  loading: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  activeType: JobsTypeFilter;
  setActiveType: React.Dispatch<React.SetStateAction<JobsTypeFilter>>;
  gigsTypeTabs: GigsTypeTab[];
  jobsCount: number;
  onToggleLike: (jobId: string) => void;
  likeUpdatingIds: Set<string>;
  onShare: (job: JobCardData) => void;
  onApply: (job: JobCardData) => void;
  onOpenAddPricingForJob: (job: JobCardData) => void;
  canApplyToJob: (eventPricingData: EventPricingApiItem[], jobEventType: string) => boolean;
  eventPricingData: EventPricingApiItem[];
}

export const GigsTabContent: React.FC<GigsTabContentProps> = ({
  jobs,
  loading,
  page,
  setPage,
  activeType,
  setActiveType,
  gigsTypeTabs,
  jobsCount,
  onToggleLike,
  likeUpdatingIds,
  onShare,
  onApply,
  onOpenAddPricingForJob,
  canApplyToJob,
  eventPricingData,
}) => {
  if (loading && jobs.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-2xl  p-12 text-center flex items-center justify-center"
      >
        <Loader size="small" />
      </motion.div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-full p-1 text-xs overflow-x-auto max-w-full gap-2">
        {gigsTypeTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveType(tab.id);
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-full transition-colors whitespace-nowrap uppercase tracking-widest font-semibold',
              activeType === tab.id
                ? 'bg-[#F33363] text-white'
                : 'text-[#F2F2F7] hover:bg-[#2C2C2E]',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <JobCardsList
        jobs={jobs}
        tabKind="gigs"
        gigsActiveType={activeType}
        hideCoinsBadge={activeType === 'express'}
        onToggleLike={onToggleLike}
        likeUpdatingIds={likeUpdatingIds}
        onShare={onShare}
        onApply={onApply}
        onOpenAddPricingForJob={onOpenAddPricingForJob}
        canApplyToJob={canApplyToJob}
        eventPricingData={eventPricingData}
      />

      {jobsCount > 0 && (() => {
        const totalPages = Math.ceil(jobsCount / GIGS_PAGE_SIZE);
        if (totalPages <= 1) return null;

        const WINDOW_SIZE = 5;
        let startPage = 1;
        let endPage = totalPages;

        if (totalPages > WINDOW_SIZE) {
          if (page <= 3) {
            startPage = 1;
            endPage = WINDOW_SIZE;
          } else if (page >= totalPages - 2) {
            endPage = totalPages;
            startPage = totalPages - (WINDOW_SIZE - 1);
          } else {
            startPage = page - 2;
            endPage = page + 2;
          }
        } else {
          startPage = 1;
          endPage = totalPages;
        }

        const pagesToRender: number[] = [];
        for (let p = startPage; p <= endPage; p += 1) pagesToRender.push(p);

        return (
          <div className="flex items-center justify-center gap-2 pt-2 pb-4">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                page <= 1 || loading
                  ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                  : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
              )}
              aria-label="Previous page"
            >
              {loading ? (
                <Loader size="small" />
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </>
              )}
            </button>
            <div className="flex items-center gap-1">
              {pagesToRender.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                  className={cn(
                    'min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-bold border transition-colors flex items-center justify-center',
                    p === page
                      ? 'border-[#F33363] bg-[#F33363] text-white'
                      : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                    loading && 'opacity-70 cursor-not-allowed'
                  )}
                  aria-label={`Page ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                >
                  {p === page && loading ? (
                    <Loader size="small" />
                  ) : (
                    p
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                page >= totalPages || loading
                  ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                  : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
              )}
              aria-label="Next page"
            >
              {loading ? (
                <Loader size="small" />
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        );
      })()}
    </div>
  );
};
